import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { MOCK_PERSONAS } from '../../constants/aiChatConstants';
// @ts-ignore
import PersonaSelector from '../../components/ailab-chat/PersonaSelector';
// @ts-ignore
import ChatWindow from '../../components/ailab-chat/ChatWindow';
import { sendMessageToBackend, getChatHistory, getPersonas } from '../../services/aiChatService';
import toast from 'react-hot-toast';
import { RefreshCw } from 'lucide-react';
import '../../components/ailab-chat/AILabChat.css';

interface Persona {
  id: string;
  koruName: string;
  systemPrompt?: string;
  avatarUrl?: string;
  koruId?: string;
  handle?: string;
}

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  personaId: string;
  timestamp: number;
  personaName?: string;
  personaAvatarUrl?: string;
}

const AiChatView: React.FC = () => {
  const { currentUser } = useAuth();
  
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(null);
  const [messagesByPersona, setMessagesByPersona] = useState<Record<string, Message[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const [loadedHistories, setLoadedHistories] = useState(new Set<string>());

  // Fetch AI Expert Personas
  const fetchExperts = useCallback(async () => {
    try {
      setConnectionError(false);
      const realPersonas = await getPersonas();

      if (realPersonas && realPersonas.length > 0) {
        setPersonas(realPersonas);
      } else {
        console.warn("Backend returned 0 personas, falling back to mocks.");
        toast("Using demo experts (Backend returned empty list)", { icon: '⚠️' });
        setPersonas(MOCK_PERSONAS);
      }
    } catch (err: any) {
      console.error("Failed to load personas:", err);
      setPersonas(MOCK_PERSONAS);
      setConnectionError(true);
      toast.error(`Connection Error: ${err.message}. Using offline demo mode.`);
    }
  }, []);

  useEffect(() => {
    fetchExperts();
  }, [fetchExperts]);

  const handleSelectPersona = async (personaId: string) => {
    setSelectedPersonaId(personaId);

    if (currentUser && currentUser.uid && !loadedHistories.has(personaId)) {
      setIsLoading(true);
      try {
        const history = await getChatHistory(currentUser.uid, personaId, personas);
        setMessagesByPersona(prev => ({
          ...prev,
          [personaId]: history,
        }));
        setLoadedHistories(prev => new Set(prev).add(personaId));
      } catch (error) {
        console.error("Failed to fetch chat history:", error);
        toast.error("Could not load chat history.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSendMessage = async (text: string) => {
    if (!selectedPersonaId || !currentUser || !currentUser.uid) {
      toast.error("Cannot send: Missing persona or user info.");
      return;
    }

    const selectedPersona = personas.find(p => p.id === selectedPersonaId);
    if (!selectedPersona) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      text,
      sender: 'user',
      personaId: selectedPersonaId,
      timestamp: Date.now(),
    };
    
    setMessagesByPersona(prev => ({
      ...prev,
      [selectedPersonaId]: [...(prev[selectedPersonaId] || []), userMessage],
    }));
    setIsLoading(true);

    try {
      const response = await sendMessageToBackend(currentUser.uid, text, selectedPersonaId);
      
      const aiMessage: Message = {
        id: `ai-${Date.now()}`,
        text: response.response,
        sender: 'ai',
        personaId: selectedPersonaId,
        timestamp: Date.now(),
        personaName: response.persona_name,
        personaAvatarUrl: selectedPersona.avatarUrl
      };
      setMessagesByPersona(prev => ({
        ...prev,
        [selectedPersonaId]: [...(prev[selectedPersonaId] || []), aiMessage],
      }));
    } catch (error: any) {
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        text: `Error: ${error.message}. (Backend may be offline)`,
        sender: 'ai',
        personaId: selectedPersonaId,
        timestamp: Date.now(),
        personaName: selectedPersona.koruName,
        personaAvatarUrl: selectedPersona.avatarUrl
      };
      setMessagesByPersona(prev => ({
        ...prev,
        [selectedPersonaId]: [...(prev[selectedPersonaId] || []), errorMessage],
      }));
      toast.error("Failed to get response from AI.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToList = () => {
    setSelectedPersonaId(null);
  };

  const selectedPersona = personas.find(p => p.id === selectedPersonaId) || null;
  const filteredMessages = messagesByPersona[selectedPersonaId || ''] || [];

  return (
    <div className="ailab-chat-container relative h-full">
      {/* Talkoru Link Banner */}
      <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs py-2 px-4 text-center z-50 shadow-md">
        <span className="font-medium">🤖 Want to chat with more AI personas? </span>
        <a 
          href="https://www.talkoru.com" 
          target="_blank" 
          rel="noopener noreferrer"
          className="underline font-bold hover:text-yellow-300 transition-colors"
        >
          Visit Talkoru.com
        </a>
        <span className="text-purple-200 ml-2">- 67+ experts including authors, philosophers, scientists & more!</span>
      </div>

      {connectionError && (
        <div className="absolute top-10 left-0 w-full bg-red-600 text-white text-xs p-2 text-center z-50 flex justify-center items-center shadow-md">
          <span>Could not connect to AI Service. Showing offline demo.</span>
          <button 
            onClick={fetchExperts} 
            className="ml-4 bg-white text-red-600 px-2 py-0.5 rounded text-xs font-bold hover:bg-gray-100 flex items-center gap-1"
          >
            <RefreshCw size={12} /> Retry
          </button>
        </div>
      )}
      
      {/* Persona Selector (Left Panel) */}
      <div className={`persona-selector-wrapper ${selectedPersonaId ? 'mobile-hidden' : 'mobile-view-container'}`} style={{marginTop: '32px'}}>
        <PersonaSelector
          personas={personas}
          selectedPersonaId={selectedPersonaId}
          onSelectPersona={handleSelectPersona}
        />
      </div>

      {/* Chat Window (Right Panel) */}
      <div className={`chat-window-wrapper ${!selectedPersonaId ? 'mobile-hidden' : 'mobile-view-container'}`} style={{marginTop: '32px'}}>
        <ChatWindow
          persona={selectedPersona}
          messages={filteredMessages}
          isLoading={isLoading}
          onSendMessage={handleSendMessage}
          onBack={handleBackToList}
        />
      </div>
    </div>
  );
};

export default AiChatView;

